import { useEffect, useState } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { signOut } from 'firebase/auth'
import { firebaseAuth, firestore } from '../firebase'
import { useAuth } from '../auth/AuthProvider'
import { PanelCard } from './panelUi'

/**
 * Account & cloud-sync panel for the PWA. Shows the signed-in user and a
 * sign-out button, plus a read-only snapshot of the Firestore library
 * document sizes (number of movies / number of series) so users can see
 * at a glance that cloud sync from the desktop has completed.
 */
export function AccountSettingsPanel() {
  const { user } = useAuth()
  const [movieCount, setMovieCount] = useState<number | null>(null)
  const [seriesCount, setSeriesCount] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    if (!user) {
      setMovieCount(null)
      setSeriesCount(null)
      return
    }
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const [m, s] = await Promise.all([
          getDoc(doc(firestore, `users/${user.uid}/library/movies`)),
          getDoc(doc(firestore, `users/${user.uid}/library/series`)),
        ])
        if (cancelled) return
        setMovieCount(m.exists() ? Object.keys(m.data() || {}).length : 0)
        setSeriesCount(s.exists() ? Object.keys(s.data() || {}).length : 0)
      } catch (err) {
        if (!cancelled) setError((err as Error).message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [user])

  return (
    <PanelCard title="Account">
      <div className="space-y-1 text-xs">
        <Row label="Signed in as" value={user?.email || '—'} />
        <Row label="User ID" value={user?.uid ?? '—'} />
        <Row
          label="Library movies"
          value={
            loading
              ? 'Loading…'
              : movieCount == null
                ? '—'
                : String(movieCount)
          }
        />
        <Row
          label="Library series"
          value={
            loading
              ? 'Loading…'
              : seriesCount == null
                ? '—'
                : String(seriesCount)
          }
        />
        {error && <p className="text-[11px] text-red-400">{error}</p>}
      </div>

      <button
        type="button"
        onClick={async () => {
          await signOut(firebaseAuth)
        }}
        className="mt-1 w-full rounded-md bg-red-500/20 px-3 py-1.5 text-xs font-medium text-red-300 hover:bg-red-500/30"
      >
        Sign out
      </button>
    </PanelCard>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3 py-0.5">
      <span className="text-text-tertiary">{label}</span>
      <span className="truncate text-right text-text-primary">{value}</span>
    </div>
  )
}
